// Central host-config (P1-14, MR-16). Single source of truth for the hardcoded
// hosts the widget composes URLs from. Refactor only — these are the EXACT
// literals previously inlined in app.vue / v4 Controls/Menu.vue / the loaders;
// this does NOT reconcile the historical post vs postv4 split (the show-metadata
// + download hosts are postv4 today, the API + RSS hosts are post), so each keeps
// its own named constant even where the value coincides.

// app.vue apiDomain default (overridable via ?apiDomain=). Playlist API host.
export const POST_API_DEFAULT = 'post.futurimedia.com';

// REMOVED: SHOW_METADATA_HOST / DOWNLOAD_HOST / RSS_HOST.
//
// These pinned the v4 show-metadata fetch, episode download and RSS-subscribe
// links to prod regardless of `?apiDomain=`, so a uat or dev-cluster embed
// offered a PRODUCTION feed and download while playing non-prod audio. Fixed on
// `master` in c8036c6; this branch never received it because the P1-14 refactor
// that created these constants was explicitly behaviour-preserving — it moved
// the literals without questioning them, so the bug survived wearing a tidier
// hat. All three routes now build off `apiPrefix` via utils/api.js.
//
// Deliberately deleted rather than left unused: a dead host constant in the file
// named "the hardcoded hosts" is an invitation to wire it back up.

// NOTE: hls.js deliberately has NO constant here any more. The live engine used
// to be fetched from an S3 mirror
// (`https://s3.amazonaws.com/widget.futuripost.com/static/hls.light.min.js`,
// uploaded by the master-only `deploy_hls_assets` CI job) — an absolute URL to an
// object that had never actually been uploaded, so `window.Hls` was undefined on
// every non-master deploy and live audio failed silently. hls.js is now VENDORED:
// the build emits it beside the hashed main bundle and src/hls_loader.js requests
// it by BARE RELATIVE NAME. It is no longer a host, so it is no longer host
// config. (Same retirement shape as VIBLAST_MIRROR_URL at LP-4/MR-78.)

// The allowlist `?apiDomain=` is bounded to. It lives here rather than beside the
// sanitiser because this file is the designated single home for host knowledge —
// the centralization guard in test/unit/hosts.spec.js enforces exactly that, and
// it caught the pattern living elsewhere.
//
// Anchored end to end, dot-separated labels only. That is what rejects the
// lookalikes a substring check waves through: userinfo (`…@attacker.example`,
// where the browser navigates to the ATTACKER), suffix extension
// (`….attacker.example`), and anything carrying a path, port, scheme or space.
//
// Measured, not guessed: 24h of postv4 traffic (5,000 iframe loads) carries
// `apiDomain` on 21% of them and every value is the prod v4 host. These two
// families cover every real embed and every cluster, with room for new ones.
export const TRUSTED_API_DOMAIN =
  /^(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)*(?:futurimedia|futuripost)\.com$/i;

// Where a widget sends its RUM, keyed on the origin the widget itself was SERVED from.
//
// 🔑 The telemetry must reach the cluster that owns the build, not production from
// everywhere. Derived from `apiDomain` (the previous behaviour) a widget on the development
// CDN reported to PRODUCTION, tagged `environment: prod` — wrong destination and wrong label.
//
// 🔑 DELIBERATELY DECOUPLED FROM `apiDomain`. A development-CDN widget pointed at
// `?apiDomain=post.futurimedia.com` now reads production data while reporting telemetry to
// avow, and that is the intended reading: the telemetry describes THIS BUILD on THIS ORIGIN.
// The origin is also what the collector's CORS allowlist is keyed on, so routing by apiDomain
// would generate requests no collector is configured to accept.
export const FARO_COLLECTOR_BY_WIDGET_HOST = {
  'widget.futuripost.com': 'post.futurimedia.com',
  // The dev distribution answers on BOTH names — `widgetv2-dev` is the original and
  // `widget-development` was added beside it so the rename needed no flag day. They must
  // resolve identically, or which collector you reach depends on which URL an embed used.
  'widget-development.futuripost.com': 'avow.futurimedia.com',
  'widgetv2-dev.futuripost.com': 'avow.futurimedia.com',
  'widget-uat.futuripost.com': 'uat.futurimedia.com',
};

// Anchored end to end, dot-separated labels only — the same shape TRUSTED_API_DOMAIN uses,
// and for the same reason: an unanchored test admits `avow.futurimedia.com.evil.example`.
const CLUSTER_HOST = /^(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)*futurimedia\.com$/i;

/**
 * The RUM collector host for a widget served from `widgetHost`, or '' for none.
 *
 * '' is a real answer, not a failure: `initFaro` treats an empty url as "do not initialise",
 * so an origin nobody has wired up produces NO telemetry rather than a console full of CORS
 * errors on the customer's page.
 */
export function resolveFaroCollectorHost(widgetHost) {
  if (!widgetHost) return '';
  const host = String(widgetHost).toLowerCase();

  const mapped = FARO_COLLECTOR_BY_WIDGET_HOST[host];
  if (mapped) return mapped;

  // Served from a cluster (still the case wherever WIDGET_APP_VERSION delivers the widget):
  // the collector is the serving host itself. Same-origin, so it needs no CORS entry, and it
  // cannot report one cluster's traffic to another.
  if (CLUSTER_HOST.test(host)) return host;

  return '';
}
